import { useCallback, useEffect, useMemo, useState } from "react";
import { Layers, Plus, Zap } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import type { FacetOp, FacetFormModel } from "../facet-form-config";
import type { FacetFormValidationErrors } from "../facet-form-helpers";
import {
  parseJsonToFormModel,
  serializeFormModelWithCommittedTransforms,
  serializeFormModelForTransformApply,
  serializeFormModelToJson,
  syncPivotTransformEditorsFromEtl,
  defaultOpForType,
  validateFormModel,
} from "../facet-form-helpers";
import { resolveNormalizeFieldKey } from "../facet-normalize-key-helpers";
import FacetFormField, { INPUT_CLASS } from "./FacetFormField";
import FacetOpSection from "./FacetOpSection";
import {
  hasUncommittedTransformDraft,
  validateTransformApply,
  type TransformAxis,
} from "../facet-transform-apply-helpers";
import type { SourceListItem } from "../../../tars-hooks/useSourceList";
import type { SourceNode } from "../../sidebar/sources-types";

interface FacetFormPanelProps {
  jsonContent: string;
  getRowValueOptions: (rowKeyHint: string) => string[];
  getNormalizeKeyValueOptions: (keyHint: string) => string[];
  columnOptions: string[];
  referenceTableOptions: string[];
  dataBankSources: SourceListItem[] | undefined;
  tarsNodes: Record<string, SourceNode>;
  onSaveAndExecute: (json: string) => void;
  onApplyTransform?: (json: string) => void;
  onNormalizeKeyCommit?: (json: string, key: string) => void;
  projectQuestionKeys?: string[];
  isApplyingTransform?: boolean;
  isLoadingNormalizeKeyValues?: boolean;
  optionsRefreshKey?: number;
  /** When set, called on Name field blur with trimmed value — syncs facet list label + JSON `etl.name`. */
  onFacetNameBlur?: (trimmedName: string) => void;
}

const EMPTY_MODEL: FacetFormModel = {
  name: "",
  decimal_places: 6,
  ds: {},
  ops: [],
};

export default function FacetFormPanel({
  jsonContent,
  getRowValueOptions,
  getNormalizeKeyValueOptions,
  columnOptions,
  referenceTableOptions,
  dataBankSources,
  tarsNodes,
  onSaveAndExecute,
  onApplyTransform,
  onNormalizeKeyCommit,
  projectQuestionKeys = [],
  isApplyingTransform = false,
  isLoadingNormalizeKeyValues = false,
  optionsRefreshKey = 0,
  onFacetNameBlur,
}: FacetFormPanelProps) {
  const { notebookId } = useParams<{ notebookId: string }>();
  const notebookIdNum = Number(notebookId);
  const [model, setModel] = useState<FacetFormModel>(EMPTY_MODEL);
  const [errors, setErrors] = useState<FacetFormValidationErrors | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [appliedJsonSnapshot, setAppliedJsonSnapshot] = useState(jsonContent);

  useEffect(() => {
    const parsed = parseJsonToFormModel(jsonContent);
    setModel(parsed ?? EMPTY_MODEL);
    setAppliedJsonSnapshot(jsonContent);
    setErrors(null);
    setInitialized(true);
  }, [jsonContent]);

  useEffect(() => {
    if (optionsRefreshKey === 0) return;
    setModel((prev) => syncPivotTransformEditorsFromEtl(prev, jsonContent));
    setAppliedJsonSnapshot(jsonContent);
  }, [optionsRefreshKey, jsonContent]);

  const canApplyTransform = useMemo(() => {
    const committedJson = appliedJsonSnapshot.trim() ? appliedJsonSnapshot : jsonContent;
    const hasDraft = hasUncommittedTransformDraft(model, committedJson);
    return hasDraft && !isApplyingTransform;
  }, [model, appliedJsonSnapshot, jsonContent, isApplyingTransform]);

  const updateModel = useCallback((updates: Partial<FacetFormModel>) => {
    setModel((prev) => ({ ...prev, ...updates }));
    setErrors(null);
  }, []);

  const handleOpChange = useCallback((index: number, op: FacetOp) => {
    setModel((prev) => ({
      ...prev,
      ops: prev.ops.map((o, i) => (i === index ? op : o)),
    }));
    setErrors(null);
  }, []);

  const handleOpRemove = useCallback((index: number) => {
    setModel((prev) => ({
      ...prev,
      ops: prev.ops.filter((_, i) => i !== index),
    }));
  }, []);

  const handleAddOp = useCallback(() => {
    setModel((prev) => ({
      ...prev,
      ops: [...prev.ops, defaultOpForType("filter")],
    }));
    toast.success("Operation created");
  }, []);

  const handleSaveAndExecute = () => {
    const validationErrors = validateFormModel(model);
    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }
    const committedJson = appliedJsonSnapshot.trim() ? appliedJsonSnapshot : jsonContent;
    const json = serializeFormModelWithCommittedTransforms(
      model,
      committedJson,
      notebookIdNum
    );
    onSaveAndExecute(json);
  };

  const handleApplyTransform = useCallback(
    (opIndex: number, axis: TransformAxis) => {
      const op = model.ops[opIndex];
      const entries = axis === "row" ? op?.row_transform : op?.col_transform;
      const message = validateTransformApply(op, axis, entries);
      if (message) {
        toast.error(message);
        return;
      }
      const json = serializeFormModelForTransformApply(
        model,
        appliedJsonSnapshot,
        { opIndex, axis },
        notebookIdNum
      );
      onApplyTransform?.(json);
    },
    [model, appliedJsonSnapshot, notebookIdNum, onApplyTransform]
  );

  const handleNormalizeKeyCommit = useCallback(
    (opIndex: number, key: string) => {
      if (!onNormalizeKeyCommit) return;
      const resolvedKey = resolveNormalizeFieldKey(key, projectQuestionKeys);
      if (resolvedKey !== key.trim() && resolvedKey) {
        toast.info(`Matched field key: ${resolvedKey}`);
      }
      const nextModel: FacetFormModel = {
        ...model,
        ops: model.ops.map((op, i) => (i === opIndex ? { ...op, key: resolvedKey } : op)),
      };
      const json = serializeFormModelToJson(nextModel, notebookIdNum);
      onNormalizeKeyCommit(json, resolvedKey);
    },
    [model, notebookIdNum, onNormalizeKeyCommit, projectQuestionKeys]
  );

  if (!initialized) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex w-full flex-col gap-5 px-3 py-4 sm:gap-6 sm:px-4 sm:py-5 lg:px-5">
          {/* Top-level fields */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_140px]">
              <FacetFormField label="Name" error={errors?.name}>
                <input
                  type="text"
                  value={model.name}
                  onChange={(e) => updateModel({ name: e.target.value })}
                  onBlur={() => onFacetNameBlur?.(model.name.trim())}
                  placeholder="e.g. Profile Age"
                  className={INPUT_CLASS}
                />
              </FacetFormField>
              <FacetFormField label="ES Index Name">
                <input
                  type="text"
                  value={
                    typeof model.ds.esindex_name === "string"
                      ? model.ds.esindex_name
                      : model.ds.esindex_name != null
                        ? String(model.ds.esindex_name)
                        : ""
                  }
                  onChange={(e) =>
                    updateModel({ ds: { ...model.ds, esindex_name: e.target.value } })
                  }
                  placeholder="e.g. p955_s2428"
                  className={INPUT_CLASS}
                />
              </FacetFormField>
              <FacetFormField
                label="Decimal Places"
                className="sm:col-span-2 lg:col-span-1"
                error={errors?.decimal_places}
              >
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={model.decimal_places}
                  onChange={(e) => updateModel({ decimal_places: Number(e.target.value) })}
                  className={INPUT_CLASS}
                />
              </FacetFormField>
            </div>
          </div>

          {/* Operations */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Layers size={14} className="text-slate-400" />
                <h3 className="text-[12px] font-bold uppercase tracking-wider text-slate-500">
                  Operations
                </h3>
                {model.ops.length > 0 && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">
                    {model.ops.length}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleAddOp}
                className="flex items-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-1.5 text-[11px] font-semibold text-indigo-600 transition hover:bg-indigo-100"
              >
                <Plus size={13} /> Add Op
              </button>
            </div>

            {model.ops.map((op, i) => (
              <FacetOpSection
                key={i}
                op={op}
                index={i}
                getRowValueOptions={getRowValueOptions}
                getNormalizeKeyValueOptions={getNormalizeKeyValueOptions}
                columnOptions={columnOptions}
                referenceTableOptions={referenceTableOptions}
                dataBankSources={dataBankSources}
                tarsNodes={tarsNodes}
                errors={errors?.ops?.[i]}
                onChange={handleOpChange}
                onRemove={handleOpRemove}
                onApplyTransform={onApplyTransform ? handleApplyTransform : undefined}
                onNormalizeKeyCommit={onNormalizeKeyCommit ? handleNormalizeKeyCommit : undefined}
                isApplyingTransform={isApplyingTransform}
                isLoadingNormalizeKeyValues={isLoadingNormalizeKeyValues}
                canApplyTransform={canApplyTransform}
                optionsRefreshKey={optionsRefreshKey}
              />
            ))}

            {model.ops.length === 0 && (
              <button
                type="button"
                onClick={handleAddOp}
                className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 py-10 transition hover:border-indigo-300 hover:bg-indigo-50/30"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-500">
                  <Plus size={20} />
                </div>
                <span className="text-[13px] font-medium text-slate-500">
                  Add your first operation
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex shrink-0 items-center justify-end gap-2.5 border-t border-slate-200 bg-gradient-to-r from-slate-50 to-white px-5 py-3">
        <button
          type="button"
          onClick={handleSaveAndExecute}
          className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-[12px] font-semibold text-white shadow-sm transition hover:bg-indigo-700 active:bg-indigo-800"
        >
          <Zap size={14} />
          Save &amp; Execute JSON
        </button>
      </div>
    </div>
  );
}
